// Shadow-DOM stylesheet. Colours follow the GRÜNE styleguide (Tanne, Klee, Grashalm, Sand,
// Himmel, Sonne). All sizes are px on purpose: host pages often change the root font
// size (e.g. html { font-size: 62.5% }), which would distort rem-based sizes.

export const CSS = /* css */ `
.gsb-root {
  all: initial;
  --tanne: #005437;
  --tanne-dark: #003d28;
  --klee: #008939;
  --grashalm: #8abd24;
  --sand: #f5f1e9;
  --sand-dark: #e6dfd0;
  --himmel: #0ba1dd;
  --sonne: #fff17a;
  --ink: #1d1d1b;
  --muted: #4d5651;
  --border: #6b746f;
  --border-light: #d9d3c7;
  --danger: #a4161a;
  --font: "PT Sans", "Source Sans 3", "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
  --font-head: "GrueneType", "PT Sans Narrow", "Arial Narrow", "PT Sans", "Segoe UI", Arial, sans-serif;
  --gsb-x: 20px;
  --gsb-y: 20px;
  --gsb-z: 2147483000;
  font-family: var(--font);
  font-size: 16px;
  line-height: 1.35;
  color: var(--ink);
  -webkit-font-smoothing: antialiased;
  -webkit-text-size-adjust: 100%;
  text-size-adjust: 100%;
}
/* Resets use :where() (zero specificity) so every component rule below wins over them. */
:where(.gsb-root) *, :where(.gsb-root) *::before, :where(.gsb-root) *::after { box-sizing: border-box; }
.gsb-root [hidden] { display: none !important; }
:where(.gsb-root) :where(button, input, textarea, select) { font: inherit; color: inherit; margin: 0; }
:where(.gsb-root) :where(button) { cursor: pointer; -webkit-tap-highlight-color: transparent; }
:where(.gsb-root) :focus { outline: none; }
:where(.gsb-root) :focus-visible { outline: 3px solid var(--tanne); outline-offset: 2px; }
:where(.gsb-root) :where(h2, h3, p, ul, ol, dl, dd, fieldset, legend) { margin: 0; padding: 0; border: 0; }
:where(.gsb-root) :where(ul, ol) { list-style: none; }
.gsb-sr { position: absolute !important; width: 1px; height: 1px; overflow: hidden; clip: rect(0 0 0 0); clip-path: inset(50%); white-space: nowrap; }

/* ---------- Launcher ---------- */
.gsb-launcher {
  position: fixed;
  z-index: var(--gsb-z);
  display: flex; align-items: center; justify-content: center; gap: 8px;
  width: 56px; height: 56px; padding: 0;
  border-radius: 50%;
  border: 2px solid var(--sand);
  background: var(--tanne);
  color: var(--sand);
  box-shadow: 0 4px 14px rgba(0, 0, 0, .28);
  transition: background-color .15s ease, transform .15s ease, right .25s ease, left .25s ease, top .25s ease, bottom .25s ease;
}
.gsb-launcher:hover { background: var(--klee); color: #fff; transform: scale(1.05); }
.gsb-launcher:focus-visible { outline: none; box-shadow: 0 0 0 3px var(--sonne), 0 0 0 6px var(--tanne); }
.gsb-launcher-label { font-weight: 700; font-size: 15px; letter-spacing: .02em; }
/* On dark pages (e.g. tools in Tanne themselves) the launcher switches to Sonne for contrast. */
.gsb-on-dark .gsb-launcher { background: var(--sonne); color: var(--tanne); border-color: var(--tanne); }
.gsb-on-dark .gsb-launcher:hover { background: #fff; color: var(--tanne); }
.gsb-on-dark .gsb-launcher:focus-visible { box-shadow: 0 0 0 3px var(--tanne), 0 0 0 6px var(--sonne); }
.gsb-launcher-bubble .gsb-launcher-label { position: absolute; width: 1px; height: 1px; overflow: hidden; clip-path: inset(50%); }
.gsb-pos-bottom-right .gsb-launcher { right: var(--gsb-x); bottom: var(--gsb-y); }
.gsb-pos-bottom-left .gsb-launcher { left: var(--gsb-x); bottom: var(--gsb-y); }
.gsb-pos-top-right .gsb-launcher { right: var(--gsb-x); top: var(--gsb-y); }
.gsb-pos-top-left .gsb-launcher { left: var(--gsb-x); top: var(--gsb-y); }

/* Tab launcher: a slim vertical tab glued to the side edge – the least obtrusive variant. */
.gsb-launcher-tab .gsb-launcher { width: 40px; height: auto; padding: 14px 0; border-radius: 10px 0 0 10px; border-right: 0; writing-mode: vertical-rl; }
.gsb-launcher-tab .gsb-launcher svg { transform: rotate(90deg); }
.gsb-launcher-tab.gsb-pos-bottom-right .gsb-launcher, .gsb-launcher-tab.gsb-pos-top-right .gsb-launcher { right: 0; }
.gsb-launcher-tab.gsb-pos-bottom-left .gsb-launcher, .gsb-launcher-tab.gsb-pos-top-left .gsb-launcher { left: 0; border-radius: 0 10px 10px 0; border-right: 2px solid var(--sand); border-left: 0; }
.gsb-launcher-tab .gsb-launcher:hover { transform: none; }

/* Minimised: only a small handle at the edge stays visible. */
.gsb-minimized .gsb-launcher { width: 22px; height: 56px; padding: 0; border-radius: 12px 0 0 12px; border-right: 0; writing-mode: horizontal-tb; }
.gsb-minimized .gsb-launcher svg { transform: none; }
.gsb-minimized.gsb-pos-bottom-right .gsb-launcher, .gsb-minimized.gsb-pos-top-right .gsb-launcher { right: 0; }
.gsb-minimized.gsb-pos-bottom-left .gsb-launcher, .gsb-minimized.gsb-pos-top-left .gsb-launcher { left: 0; border-radius: 0 12px 12px 0; border-left: 0; border-right: 2px solid var(--sand); }
.gsb-minimized .gsb-launcher:hover { transform: none; width: 28px; }
.gsb-minimized .gsb-launcher-label { position: absolute; width: 1px; height: 1px; overflow: hidden; clip-path: inset(50%); }

.gsb-minimize {
  position: fixed;
  z-index: var(--gsb-z);
  width: 24px; height: 24px; padding: 0;
  display: flex; align-items: center; justify-content: center;
  border-radius: 50%;
  border: 1.5px solid var(--tanne);
  background: #fff;
  color: var(--tanne);
  opacity: 0;
  transition: opacity .15s ease;
}
.gsb-root:hover .gsb-minimize, .gsb-minimize:focus-visible, .gsb-launcher:focus-visible ~ .gsb-minimize { opacity: 1; }
@media (hover: none) { .gsb-minimize { opacity: 1; } }
.gsb-pos-bottom-right .gsb-minimize { right: calc(var(--gsb-x) - 6px); bottom: calc(var(--gsb-y) + 40px); }
.gsb-pos-bottom-left .gsb-minimize { left: calc(var(--gsb-x) - 6px); bottom: calc(var(--gsb-y) + 40px); }
.gsb-pos-top-right .gsb-minimize { right: calc(var(--gsb-x) - 6px); top: calc(var(--gsb-y) + 40px); }
.gsb-pos-top-left .gsb-minimize { left: calc(var(--gsb-x) - 6px); top: calc(var(--gsb-y) + 40px); }
.gsb-minimized .gsb-minimize, .gsb-launcher-tab .gsb-minimize, .gsb-open .gsb-minimize, .gsb-launcher-hidden .gsb-minimize { display: none; }
.gsb-launcher-hidden .gsb-launcher { display: none; }
.gsb-typing:not(.gsb-open) .gsb-launcher, .gsb-typing .gsb-minimize { visibility: hidden; }

/* ---------- Panel ---------- */
.gsb-panel {
  position: fixed;
  z-index: var(--gsb-z);
  width: 380px;
  max-width: calc(100vw - 24px);
  max-height: min(700px, calc(100vh - var(--gsb-y) - 92px));
  display: flex; flex-direction: column;
  background: #fff;
  color: var(--ink);
  border-radius: 12px;
  border: 1px solid rgba(0, 84, 55, .25);
  box-shadow: 0 14px 44px rgba(0, 0, 0, .3);
  overflow: hidden;
  animation: gsb-in .16s ease-out;
}
.gsb-pos-bottom-right .gsb-panel { right: var(--gsb-x); bottom: calc(var(--gsb-y) + 70px); }
.gsb-pos-bottom-left .gsb-panel { left: var(--gsb-x); bottom: calc(var(--gsb-y) + 70px); }
.gsb-pos-top-right .gsb-panel { right: var(--gsb-x); top: calc(var(--gsb-y) + 70px); }
.gsb-pos-top-left .gsb-panel { left: var(--gsb-x); top: calc(var(--gsb-y) + 70px); }
.gsb-launcher-tab.gsb-pos-bottom-right .gsb-panel, .gsb-launcher-tab.gsb-pos-top-right .gsb-panel { right: 52px; }
.gsb-launcher-tab.gsb-pos-bottom-left .gsb-panel, .gsb-launcher-tab.gsb-pos-top-left .gsb-panel { left: 52px; }
.gsb-launcher-tab.gsb-pos-bottom-right .gsb-panel, .gsb-launcher-tab.gsb-pos-bottom-left .gsb-panel { bottom: var(--gsb-y); }
.gsb-launcher-tab.gsb-pos-top-right .gsb-panel, .gsb-launcher-tab.gsb-pos-top-left .gsb-panel { top: var(--gsb-y); }
.gsb-launcher-tab .gsb-panel, .gsb-launcher-hidden .gsb-panel { max-height: min(700px, calc(100vh - var(--gsb-y) - 16px)); }
.gsb-launcher-hidden.gsb-pos-bottom-right .gsb-panel, .gsb-launcher-hidden.gsb-pos-bottom-left .gsb-panel { bottom: var(--gsb-y); }
.gsb-launcher-hidden.gsb-pos-top-right .gsb-panel, .gsb-launcher-hidden.gsb-pos-top-left .gsb-panel { top: var(--gsb-y); }

@media (max-width: 520px) {
  .gsb-root .gsb-panel { left: 8px; right: 8px; bottom: 8px; top: auto; width: auto; max-width: none; max-height: calc(100vh - 16px); max-height: calc(100dvh - 16px); }
  .gsb-open .gsb-launcher { display: none; }
}
@keyframes gsb-in { from { opacity: 0; transform: translateY(8px); } }

.gsb-head { display: flex; align-items: flex-start; gap: 8px; padding: 14px 10px 12px 16px; background: var(--tanne); color: var(--sand); }
.gsb-head-text { flex: 1; min-width: 0; }
.gsb-title { font-family: var(--font-head); font-weight: 900; font-size: 21px; line-height: 1.1; text-transform: uppercase; letter-spacing: .01em; }
.gsb-subtitle { font-size: 14px; margin-top: 3px; opacity: .95; }
.gsb-icon-btn { flex: none; width: 40px; height: 40px; display: inline-flex; align-items: center; justify-content: center; border: 0; border-radius: 8px; background: transparent; color: inherit; padding: 0; }
.gsb-icon-btn:hover { background: rgba(255, 255, 255, .14); }
.gsb-head .gsb-icon-btn:focus-visible { outline-color: var(--sonne); outline-offset: -3px; }

.gsb-toolline { display: flex; flex-wrap: wrap; align-items: baseline; gap: 2px 8px; padding: 10px 16px; background: var(--sand); color: var(--tanne); font-size: 14px; border-bottom: 1px solid var(--sand-dark); }
.gsb-toolline strong { font-weight: 700; font-size: 15px; }
.gsb-toolline .gsb-provider { color: var(--muted); }
.gsb-toolline label { display: block; width: 100%; font-weight: 700; margin-bottom: 4px; }

.gsb-body { flex: 1; min-height: 0; overflow-y: auto; overscroll-behavior: contain; padding: 16px; }
.gsb-foot { display: flex; justify-content: space-between; gap: 8px; padding: 8px 16px; border-top: 1px solid var(--border-light); font-size: 13px; color: var(--muted); }
.gsb-foot a { color: var(--tanne); }

/* ---------- Controls ---------- */
.gsb-btn { display: inline-flex; align-items: center; justify-content: center; gap: 8px; min-height: 44px; padding: 10px 18px; border-radius: 8px; border: 2px solid transparent; font-weight: 700; font-size: 16px; line-height: 1.1; text-decoration: none; transition: background-color .15s ease, color .15s ease; }
.gsb-btn-primary { background: var(--tanne); color: var(--sand); }
.gsb-btn-primary:hover { background: var(--klee); color: #fff; }
.gsb-btn-secondary { background: #fff; color: var(--tanne); border-color: var(--tanne); }
.gsb-btn-secondary:hover { background: var(--sand); }
.gsb-btn-ghost-light { background: transparent; color: var(--sand); border-color: var(--sand); }
.gsb-btn-ghost-light:hover { background: rgba(255, 255, 255, .14); }
.gsb-btn-small { min-height: 36px; padding: 6px 12px; font-size: 14px; }
.gsb-btn-block { width: 100%; }
.gsb-btn[disabled] { opacity: .65; cursor: progress; }
.gsb-link { display: inline; min-height: 0; padding: 0; border: 0; background: none; color: var(--tanne); font-size: inherit; text-decoration: underline; text-underline-offset: 2px; }
.gsb-link:hover { color: var(--klee); }

.gsb-choice { display: flex; align-items: center; gap: 14px; width: 100%; margin-bottom: 10px; padding: 14px; text-align: left; border: 2px solid var(--sand-dark); border-radius: 10px; background: var(--sand); color: var(--tanne); }
.gsb-choice:hover { border-color: var(--klee); }
.gsb-choice-icon { flex: none; width: 46px; height: 46px; display: flex; align-items: center; justify-content: center; border-radius: 50%; background: var(--tanne); color: var(--sonne); }
.gsb-choice-title { display: block; font-weight: 700; font-size: 17px; }
.gsb-choice-hint { display: block; font-size: 14px; color: var(--muted); margin-top: 2px; }
.gsb-recipient { display: flex; gap: 8px; align-items: flex-start; margin-top: 6px; font-size: 14px; color: var(--muted); }
.gsb-recipient svg { flex: none; color: var(--tanne); margin-top: 1px; }
.gsb-help { display: inline-block; margin-top: 12px; color: var(--tanne); font-size: 15px; }

.gsb-form-head { display: flex; align-items: center; gap: 6px; margin: -6px 0 12px -8px; }
.gsb-form-head .gsb-icon-btn { color: var(--tanne); }
.gsb-form-head .gsb-icon-btn:hover { background: var(--sand); }
.gsb-h { font-family: var(--font-head); font-weight: 900; font-size: 19px; color: var(--tanne); text-transform: uppercase; line-height: 1.1; }
.gsb-field { margin-bottom: 16px; }
.gsb-label { display: block; margin-bottom: 4px; font-weight: 700; font-size: 15px; color: var(--tanne); }
.gsb-hint { margin: 0 0 6px; font-size: 14px; color: var(--muted); }
.gsb-input, .gsb-textarea, .gsb-select { display: block; width: 100%; padding: 10px 12px; font-size: 16px; line-height: 1.35; border: 1.5px solid var(--border); border-radius: 8px; background: #fff; color: var(--ink); }
.gsb-select { padding-right: 32px; }
.gsb-textarea { min-height: 112px; resize: vertical; }
.gsb-input:focus-visible, .gsb-textarea:focus-visible, .gsb-select:focus-visible { outline-offset: 0; border-color: var(--tanne); }
.gsb-input[aria-invalid="true"], .gsb-textarea[aria-invalid="true"] { border-color: var(--danger); }

.gsb-section-title { margin: 4px 0 8px; font-weight: 700; font-size: 15px; color: var(--tanne); }
.gsb-check { display: flex; align-items: center; gap: 10px; min-height: 36px; font-size: 15px; cursor: pointer; }
.gsb-check input { width: 20px; height: 20px; accent-color: var(--tanne); flex: none; }
.gsb-shot { display: flex; align-items: center; gap: 12px; margin: 6px 0 10px; padding: 10px; border: 1px solid var(--border-light); border-radius: 10px; background: #fff; font-size: 14px; color: var(--muted); }
.gsb-shot canvas { flex: none; width: 112px; height: 70px; object-fit: cover; object-position: top left; border: 1px solid var(--border-light); border-radius: 4px; background: var(--sand); }
.gsb-thumb-btn { flex: none; padding: 0; border: 0; background: none; border-radius: 4px; cursor: zoom-in; }
.gsb-shot-actions { display: flex; flex-wrap: wrap; gap: 4px 12px; }
.gsb-shot-status { display: flex; align-items: center; gap: 10px; }
.gsb-spinner { flex: none; width: 18px; height: 18px; border: 3px solid var(--sand-dark); border-top-color: var(--tanne); border-radius: 50%; animation: gsb-spin .8s linear infinite; }
@keyframes gsb-spin { to { transform: rotate(360deg); } }

.gsb-picks { margin: 8px 0 0; }
.gsb-picks li { display: flex; align-items: center; gap: 10px; margin-top: 6px; padding: 6px 6px 6px 8px; border-radius: 8px; background: var(--sand); font-size: 14px; color: var(--tanne); }
.gsb-picks .gsb-pick-text { flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.gsb-num { flex: none; width: 24px; height: 24px; display: inline-flex; align-items: center; justify-content: center; border-radius: 50%; background: var(--tanne); color: var(--sonne); font-weight: 700; font-size: 13px; }
.gsb-picks .gsb-icon-btn { width: 32px; height: 32px; color: var(--tanne); }
.gsb-picks .gsb-icon-btn:hover { background: var(--sand-dark); }

.gsb-details { margin: 4px 0 16px; font-size: 14px; color: var(--muted); }
.gsb-details summary { cursor: pointer; color: var(--tanne); padding: 6px 0; }
.gsb-details dl { display: grid; grid-template-columns: max-content 1fr; gap: 4px 12px; margin-top: 6px; padding: 10px 12px; background: var(--sand); border-radius: 8px; }
.gsb-details dt { font-weight: 700; color: var(--tanne); }
.gsb-details dd { overflow-wrap: anywhere; }

.gsb-error { margin: 0 0 12px; padding: 10px 12px; border-left: 4px solid var(--danger); background: #fbeaea; color: var(--danger); font-size: 14px; }
.gsb-copy { margin: -4px 0 12px; }
.gsb-notice { margin: 10px 0 4px; }
.gsb-honeypot { position: absolute !important; left: -10000px; width: 1px; height: 1px; overflow: hidden; }

.gsb-success { text-align: center; padding: 8px 0; }
.gsb-success-icon { width: 64px; height: 64px; margin: 4px auto 12px; display: flex; align-items: center; justify-content: center; border-radius: 50%; background: var(--grashalm); color: var(--tanne); }
.gsb-success p { margin: 6px 0; }
.gsb-ref { display: inline-flex; flex-direction: column; align-items: center; gap: 2px; margin: 10px 0 16px; padding: 8px 16px; border-radius: 8px; background: var(--sand); color: var(--tanne); }
.gsb-ref-label { font-size: 13px; color: var(--muted); }
.gsb-ref-id { font-weight: 700; font-size: 18px; letter-spacing: .03em; white-space: nowrap; }
.gsb-actions { display: flex; flex-wrap: wrap; justify-content: center; gap: 8px; }

/* Hidden while the picker / browser capture is active. */
.gsb-busy .gsb-panel, .gsb-busy .gsb-launcher, .gsb-busy .gsb-minimize { visibility: hidden; }

/* ---------- Element picker ---------- */
.gsb-pick-layer { position: fixed; inset: 0; z-index: var(--gsb-z); cursor: crosshair; background: transparent; touch-action: manipulation; }
.gsb-pick-hover, .gsb-pick-marker { position: fixed; top: 0; left: 0; z-index: var(--gsb-z); pointer-events: none; border: 3px solid var(--sonne); outline: 3px solid var(--tanne); border-radius: 4px; }
.gsb-pick-hover { background: rgba(255, 241, 122, .2); border-style: dashed; }
.gsb-pick-hover-selected { background: transparent; }
.gsb-pick-badge { position: absolute; top: -15px; left: -15px; width: 26px; height: 26px; display: flex; align-items: center; justify-content: center; border-radius: 50%; border: 2px solid var(--sonne); background: var(--tanne); color: var(--sonne); font-weight: 700; font-size: 13px; }
.gsb-pick-bar { position: fixed; top: 12px; left: 50%; transform: translateX(-50%); z-index: var(--gsb-z); width: min(760px, calc(100vw - 24px)); display: flex; align-items: center; gap: 12px; padding: 10px 12px 10px 14px; border-radius: 12px; background: var(--tanne); color: var(--sand); font-size: 15px; box-shadow: 0 8px 28px rgba(0, 0, 0, .3); }
.gsb-pick-bar.gsb-pick-bar-bottom { top: auto; bottom: 12px; }
.gsb-pick-icon { flex: none; color: var(--sonne); display: flex; }
.gsb-pick-text { flex: 1; }
.gsb-pick-count { font-weight: 700; color: var(--sonne); }
.gsb-pick-actions { display: flex; gap: 8px; flex: none; }
.gsb-pick-bar .gsb-btn-primary { background: var(--sonne); color: var(--tanne); }
.gsb-pick-bar .gsb-btn-primary:hover { background: #fff; color: var(--tanne); }
.gsb-pick-bar :focus-visible { outline-color: var(--sonne); }
.gsb-pick-move { color: var(--sand); }
@media (max-width: 520px) { .gsb-pick-bar { flex-wrap: wrap; } .gsb-pick-actions { width: 100%; justify-content: flex-end; } }

/* ---------- Screenshot editor ---------- */
.gsb-editor-backdrop { position: fixed; inset: 0; z-index: var(--gsb-z); display: flex; align-items: center; justify-content: center; padding: 16px; background: rgba(0, 30, 20, .74); }
.gsb-editor { display: flex; flex-direction: column; max-width: min(1200px, 100%); max-height: 100%; overflow: hidden; border-radius: 12px; background: #fff; box-shadow: 0 14px 44px rgba(0, 0, 0, .4); }
.gsb-editor-head { padding: 12px 16px; background: var(--tanne); color: var(--sand); }
.gsb-editor-title { font-family: var(--font-head); font-weight: 900; font-size: 19px; text-transform: uppercase; }
.gsb-editor-hint { margin-top: 2px; font-size: 14px; }
.gsb-editor-tools { display: flex; flex-wrap: wrap; gap: 8px; padding: 10px 16px; background: var(--sand); }
.gsb-tool { display: inline-flex; align-items: center; gap: 6px; min-height: 40px; padding: 6px 12px; border-radius: 8px; border: 2px solid var(--tanne); background: #fff; color: var(--tanne); font-weight: 700; font-size: 15px; }
.gsb-tool[aria-pressed="true"] { background: var(--tanne); color: var(--sand); }
.gsb-tool:disabled { opacity: .5; cursor: default; }
.gsb-editor-stage { flex: 1; min-height: 0; overflow: auto; display: flex; justify-content: center; align-items: flex-start; padding: 12px; background: var(--sand-dark); }
.gsb-editor-canvas { display: block; max-width: 100%; max-height: calc(100vh - 240px); width: auto; height: auto; cursor: crosshair; touch-action: none; box-shadow: 0 2px 10px rgba(0, 0, 0, .2); }
.gsb-editor-foot { display: flex; justify-content: flex-end; padding: 10px 16px; }

@media (prefers-reduced-motion: reduce) {
  .gsb-root *, .gsb-root *::before, .gsb-root *::after { animation: none !important; transition: none !important; }
}
@media print { .gsb-root { display: none !important; } }
@media (forced-colors: active) {
  .gsb-launcher, .gsb-panel, .gsb-pick-bar, .gsb-tool { border: 2px solid CanvasText; }
}
`;
